import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  submitArticleOpinion,
  getArticleOpinionStats,
  getUserArticleOpinion
} from '../article-opinion';

type OpinionType = 'OPPOSE' | 'SLIGHTLY_OPPOSE' | 'SLIGHTLY_AGREE' | 'AGREE';

interface OpinionStats {
  OPPOSE: number;
  SLIGHTLY_OPPOSE: number;
  SLIGHTLY_AGREE: number;
  AGREE: number;
}

interface ArticleOpinion {
  id: string;
  type: OpinionType;
  userId: string;
  articleId: string;
  createdAt: string;
  updatedAt: string;
}

const opinionKeys = {
  all: ['article-opinion'] as const,
  stats: (articleId: string) => [...opinionKeys.all, 'stats', articleId] as const,
  userOpinion: (articleId: string) => [...opinionKeys.all, 'user', articleId] as const
};

export const useArticleOpinionStats = (articleId: string) => {
  return useQuery({
    queryKey: opinionKeys.stats(articleId),
    queryFn: () => getArticleOpinionStats(articleId),
    staleTime: 1000 * 60 // Consider stats fresh for 1 minute
  });
};

export const useUserArticleOpinion = (articleId: string) => {
  return useQuery({
    queryKey: opinionKeys.userOpinion(articleId),
    queryFn: () => getUserArticleOpinion(articleId),
    staleTime: 1000 * 60 * 5 // Consider user's opinion fresh for 5 minutes
  });
};

export const useSubmitArticleOpinion = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { articleId: string; type: string }) => submitArticleOpinion(data),
    onMutate: async ({ articleId, type }) => {
      await queryClient.cancelQueries({ queryKey: opinionKeys.userOpinion(articleId) });
      await queryClient.cancelQueries({ queryKey: opinionKeys.stats(articleId) });

      // Snapshot previous values
      const previousOpinion = queryClient.getQueryData<ArticleOpinion>(
        opinionKeys.userOpinion(articleId)
      );
      const previousStats = queryClient.getQueryData<OpinionStats>(opinionKeys.stats(articleId));

      // Optimistically update user opinion
      queryClient.setQueryData(opinionKeys.userOpinion(articleId), {
        ...previousOpinion,
        type
      });

      // Optimistically update stats if we have previous stats
      if (previousStats) {
        const newStats = { ...previousStats };
        if (previousOpinion) {
          // Decrement previous opinion count
          newStats[previousOpinion.type] -= 1;
        }
        // Increment new opinion count
        newStats[type] = (newStats[type] || 0) + 1;

        queryClient.setQueryData(opinionKeys.stats(articleId), newStats);
      }

      return { previousOpinion, previousStats };
    },

    onError: (err, { articleId }, context) => {
      if (context?.previousOpinion) {
        queryClient.setQueryData(opinionKeys.userOpinion(articleId), context.previousOpinion);
      }
      if (context?.previousStats) {
        queryClient.setQueryData(opinionKeys.stats(articleId), context.previousStats);
      }
    },

    onSettled: (data, error, { articleId }) => {
      queryClient.invalidateQueries({ queryKey: opinionKeys.userOpinion(articleId) });
      queryClient.invalidateQueries({ queryKey: opinionKeys.stats(articleId) });
    }
  });
};
